/**
 * Work order / ticket activity, grouped by vendor and by property — cost
 * included. Nav-placed inside the "Maintenance" group in StaffLayout.tsx,
 * but gated `reports:read` like PaymentsHistory.tsx, not `maintenance:read`
 * like its Work orders/Vendors siblings: this report surfaces the `cost`
 * figure across the whole portfolio, the same "financial reports are not
 * for MAINTENANCE" line CLAUDE.md already draws for the Reports module.
 * MAINTENANCE-role staff keep the nav group but not this one link — same
 * shape Payments History takes under Payment Center.
 *
 * Live, not a frozen artifact: an optional date range, defaulting to all
 * time. Two `useList` calls (by-vendor, by-property) sharing one filter
 * row, switched by a local tab — same `.tabs`/`.tab--active` vocabulary
 * ReportsHome.tsx uses one level up, without pulling this page into that
 * shell (its `reports:read` gate lives on this standalone route instead).
 */

import { useState } from 'react';
import { useList } from '@refinedev/core';
import { DataTable, type Column } from '../../components/DataTable.js';
import { ReportFilterBar } from '../../components/ReportFilterBar.js';

interface VendorOption {
  id: string;
  name: string;
}

interface MaintenanceByVendorRow {
  vendorId: string | null;
  vendorName: string;
  ticketCount: number;
  openCount: number;
  completedCount: number;
  avgDaysToComplete: number | null;
  totalCost: string;
  avgCost: string | null;
}

interface MaintenanceByPropertyRow {
  propertyId: string;
  propertyName: string;
  ticketCount: number;
  openCount: number;
  completedCount: number;
  avgDaysToComplete: number | null;
  totalCost: string;
  avgCost: string | null;
}

const vendorColumns: Column<MaintenanceByVendorRow>[] = [
  { key: 'vendor', header: 'Vendor', render: (r) => r.vendorName },
  { key: 'tickets', header: 'Tickets', numeric: true, render: (r) => r.ticketCount },
  { key: 'open', header: 'Open', numeric: true, render: (r) => r.openCount },
  { key: 'completed', header: 'Completed', numeric: true, render: (r) => r.completedCount },
  {
    key: 'avgDays',
    header: 'Avg days to complete',
    numeric: true,
    render: (r) => r.avgDaysToComplete ?? '—',
  },
  { key: 'totalCost', header: 'Total cost', numeric: true, render: (r) => `$${r.totalCost}` },
  {
    key: 'avgCost',
    header: 'Avg cost',
    numeric: true,
    render: (r) => (r.avgCost ? `$${r.avgCost}` : '—'),
  },
];

const propertyColumns: Column<MaintenanceByPropertyRow>[] = [
  { key: 'property', header: 'Property', render: (r) => r.propertyName },
  { key: 'tickets', header: 'Tickets', numeric: true, render: (r) => r.ticketCount },
  { key: 'open', header: 'Open', numeric: true, render: (r) => r.openCount },
  { key: 'completed', header: 'Completed', numeric: true, render: (r) => r.completedCount },
  {
    key: 'avgDays',
    header: 'Avg days to complete',
    numeric: true,
    render: (r) => r.avgDaysToComplete ?? '—',
  },
  { key: 'totalCost', header: 'Total cost', numeric: true, render: (r) => `$${r.totalCost}` },
  {
    key: 'avgCost',
    header: 'Avg cost',
    numeric: true,
    render: (r) => (r.avgCost ? `$${r.avgCost}` : '—'),
  },
];

export function MaintenanceReports() {
  const [tab, setTab] = useState<'vendor' | 'property'>('vendor');
  const [propertyId, setPropertyId] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [vendorId, setVendorId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const { data: vendorsData } = useList<VendorOption>({
    resource: 'vendors',
    pagination: { current: 1, pageSize: 200 },
  });

  const filters = [
    { field: 'propertyId', operator: 'eq' as const, value: propertyId || undefined },
    { field: 'ownerId', operator: 'eq' as const, value: ownerId || undefined },
    { field: 'vendorId', operator: 'eq' as const, value: vendorId || undefined },
    { field: 'from', operator: 'eq' as const, value: from || undefined },
    { field: 'to', operator: 'eq' as const, value: to || undefined },
  ];

  const byVendor = useList<MaintenanceByVendorRow>({
    resource: 'reports/maintenance-by-vendor',
    pagination: { mode: 'off' },
    filters,
    queryOptions: { enabled: tab === 'vendor' },
  });

  const byProperty = useList<MaintenanceByPropertyRow>({
    resource: 'reports/maintenance-by-property',
    pagination: { mode: 'off' },
    filters,
    queryOptions: { enabled: tab === 'property' },
  });

  const exportPath =
    tab === 'vendor' ? '/reports/maintenance-by-vendor' : '/reports/maintenance-by-property';

  return (
    <section className="stack">
      <h1>Maintenance reports</h1>
      <p className="muted">
        Work order activity and cost, grouped by vendor or by property. Live data — narrow to a date
        range, a property, an owner&apos;s properties, or one vendor below.
      </p>

      <ReportFilterBar
        propertyId={propertyId}
        onPropertyChange={setPropertyId}
        ownerId={ownerId}
        onOwnerChange={setOwnerId}
        exportPath={exportPath}
        exportParams={{
          propertyId: propertyId || undefined,
          ownerId: ownerId || undefined,
          vendorId: vendorId || undefined,
          from: from || undefined,
          to: to || undefined,
        }}
      >
        <select
          className="field__input"
          value={vendorId}
          onChange={(event) => setVendorId(event.target.value)}
        >
          <option value="">All vendors</option>
          {(vendorsData?.data ?? []).map((vendor) => (
            <option key={vendor.id} value={vendor.id}>
              {vendor.name}
            </option>
          ))}
        </select>
        <label className="row">
          <span className="muted">From</span>
          <input
            className="field__input"
            type="date"
            value={from}
            onChange={(event) => setFrom(event.target.value)}
          />
        </label>
        <label className="row">
          <span className="muted">To</span>
          <input
            className="field__input"
            type="date"
            value={to}
            onChange={(event) => setTo(event.target.value)}
          />
        </label>
      </ReportFilterBar>

      <div className="tabs" role="tablist" aria-label="Maintenance reports">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'vendor'}
          className={tab === 'vendor' ? 'tab tab--active' : 'tab'}
          onClick={() => setTab('vendor')}
        >
          By vendor
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'property'}
          className={tab === 'property' ? 'tab tab--active' : 'tab'}
          onClick={() => setTab('property')}
        >
          By property
        </button>
      </div>

      {tab === 'vendor' ? (
        <DataTable
          columns={vendorColumns}
          rows={byVendor.data?.data ?? []}
          rowKey={(row) => row.vendorId ?? 'unassigned'}
          isLoading={byVendor.isLoading}
          error={byVendor.error}
          emptyMessage="No work order activity for this filter."
        />
      ) : (
        <DataTable
          columns={propertyColumns}
          rows={byProperty.data?.data ?? []}
          rowKey={(row) => row.propertyId}
          isLoading={byProperty.isLoading}
          error={byProperty.error}
          emptyMessage="No work order activity for this filter."
        />
      )}
    </section>
  );
}
